import { Tooltip, Spin } from "antd";
import { LoadingOutlined, FileTextOutlined } from "@ant-design/icons";
import { BsFileEarmarkX, BsCaretRightFill } from "react-icons/bs";
import { CiEdit, CiUser, CiCalendar, CiImport, CiPause1 } from "react-icons/ci";
import { AiOutlineRotateRight, AiOutlineDoubleLeft, AiOutlineDoubleRight } from "react-icons/ai";
import { PiBarcodeThin, PiHandshakeThin, PiPixLogoThin } from "react-icons/pi";
import { SlEarphonesAlt } from "react-icons/sl";
import { RxUpdate } from "react-icons/rx";
import { LiaUndoAltSolid } from "react-icons/lia";
import { BiArrowBack } from "react-icons/bi";
import styles from "./BarraAcoesOS.module.css";

/**
 * Barra lateral com os botões de ação da OS. Toda a decisão de "qual botão
 * aparece" já vem pronta em `botoes` (calculada em estadoOS.js) — este
 * componente só renderiza.
 */
export function BarraAcoesOS({
  botoes,
  loading,
  info,
  reportleads,
  mergedArrow,
  styledBtn,
  styledBtnImport,
  mostrarBotaoVoltar,
  onVoltar,
  onReportarLeads,
  onAvancarPerfilVendas,
  onNegociar,
  onIniciarNegociacao,
  onRetroceder,
  onAvancar,
  onDesnegociar,
  onEditarObservacao,
  onAtribuirAnalista,
  onTrocarCliente,
  onAtualizarCliente,
  onDataMigracao,
  onDataEncerramento,
  onGerarBoleto,
  loadingGerarBoleto,
  onGerarPix,
  loadingGerarPix,
  onCompletarOS,
  onCarregarDocumento,
  onImportAberturaAlteracao,
  onImportEnquadramento,
  onImportBaixaEncerramento,
  importsBloqueados,
  onCancelar,
  onRestaurar,
  onSuspender,
}) {
  // styledBtn/styledBtnImport continuam sendo aceitos como props (podem
  // carregar classes extras vindas do componente pai) e são combinados com
  // as classes do CSS Module.
  const btnBase = [styles.btnOS, styledBtn].filter(Boolean).join(" ");
  const btnImportBase = [styles.btnImport, styledBtnImport].filter(Boolean).join(" ");

  return (
    <div className={`controlers ${styles.controlers}`}>
      {mostrarBotaoVoltar && (
        <Tooltip placement="right" title="Voltar" arrow={mergedArrow}>
          <button className={btnBase} onClick={onVoltar}>
            <BiArrowBack className="font-icon" />
          </button>
        </Tooltip>
      )}

      {reportleads && (
        <Tooltip placement="right" title="Reportar Leads" arrow={mergedArrow}>
          <button className={btnBase} onClick={onReportarLeads} disabled={loading}>
            <FileTextOutlined className="font-icon" />
          </button>
        </Tooltip>
      )}

      {reportleads && botoes.isBtnAvancarPerfilVendas && (
        <Tooltip placement="right" title="Avançar OS (Ação pelo Comercial)" arrow={mergedArrow}>
          <button className={styles.corComercial} disabled={loading} onClick={onAvancarPerfilVendas}>
            <AiOutlineDoubleRight className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnNegociarOS && (
        <Tooltip placement="right" title="Negociação OS" arrow={mergedArrow}>
          <button className={btnBase} onClick={onNegociar} disabled={loading}>
            <SlEarphonesAlt className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnIniciarNegociacao && (
        <Tooltip placement="right" title="Iniciar Negociação OS" arrow={mergedArrow}>
          <button className={btnBase} onClick={onIniciarNegociacao} disabled={loading}>
            <SlEarphonesAlt className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnRetrocederOS && (
        <Tooltip placement="right" title={loading ? "Loading..." : "Retroceder OS"} arrow={mergedArrow}>
          <button className={btnBase} onClick={loading ? undefined : onRetroceder} disabled={loading}>
            {loading && <Spin indicator={<LoadingOutlined spin />} size="small" />}
            <AiOutlineDoubleLeft className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnAvancarOS && (
        <Tooltip placement="right" title={loading ? "Loading..." : "Avançar OS"} arrow={mergedArrow}>
          <button
            className={`${styles.btnOS} ${importsBloqueados ? styles.btnDisabled : btnBase}`}
            onClick={loading ? undefined : onAvancar}
            disabled={loading || importsBloqueados}
          >
            {loading && <Spin indicator={<LoadingOutlined spin />} size="small" />}
            <AiOutlineDoubleRight className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnDesnegociar && (
        <Tooltip placement="right" title="Desfazer Negociação OS" arrow={mergedArrow}>
          <button className={btnBase} onClick={onDesnegociar} disabled={loading}>
            <LiaUndoAltSolid className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnEditarObservacaoOS && (
        <Tooltip placement="right" title="Editar Observação" arrow={mergedArrow}>
          <button className={btnBase} onClick={onEditarObservacao} disabled={loading}>
            <CiEdit className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnAtribuirAnalistaOS && (
        <Tooltip placement="right" title="Atribuir Analista" arrow={mergedArrow}>
          <button className={btnBase} onClick={onAtribuirAnalista} disabled={loading}>
            <CiUser className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnTrocarClienteOS && (
        <Tooltip
          placement="right"
          title={info.clienteId === "" || info.clienteId === null ? "Gerar Cliente" : "Trocar Cliente"}
          arrow={mergedArrow}
        >
          <button className={btnBase} onClick={onTrocarCliente} disabled={loading}>
            <AiOutlineRotateRight className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnAtualizarClienteOS && (
        <Tooltip placement="right" title="Atualizar Empresa" arrow={mergedArrow}>
          <button className={btnBase} onClick={onAtualizarCliente} disabled={loading}>
            <RxUpdate className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnDataMigracao && (
        <Tooltip placement="right" title="Data Migração" arrow={mergedArrow}>
          <button className={btnBase} onClick={onDataMigracao} disabled={loading}>
            <CiCalendar className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnDataEncerramento && (
        <Tooltip placement="right" title="Data Encerramento" arrow={mergedArrow}>
          <button className={btnBase} onClick={onDataEncerramento} disabled={loading}>
            <CiCalendar className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnGerarBoletoOS && (
        <Tooltip placement="right" title="Gerar Boleto" arrow={mergedArrow}>
          <button className={btnBase} disabled={loadingGerarBoleto} onClick={loadingGerarBoleto ? undefined : onGerarBoleto}>
            {loadingGerarBoleto && <Spin indicator={<LoadingOutlined spin />} size="small" />}
            <PiBarcodeThin className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnGerarPixOS && (
        <Tooltip placement="right" title="Gerar PIX" arrow={mergedArrow}>
          <button className={btnBase} onClick={loadingGerarPix ? undefined : onGerarPix} disabled={loadingGerarPix}>
            {loadingGerarPix && <Spin indicator={<LoadingOutlined spin />} size="small" />}
            <PiPixLogoThin className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnCompletarOS && (
        <Tooltip placement="right" title="Completar OS" arrow={mergedArrow}>
          <button className={btnBase} disabled={loading} onClick={onCompletarOS}>
            <PiHandshakeThin className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnCarregadorDocOS && (
        <Tooltip placement="right" title="Carregar Documento" arrow={mergedArrow}>
          <button className={btnBase} onClick={onCarregarDocumento} disabled={loading}>
            <CiImport className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnModalImportsAberturaAlteracaoEmpresa && (
        <Tooltip placement="right" title="Carregar Arquivo para Abertura de Empresa" arrow={mergedArrow}>
          <button className={btnImportBase} onClick={onImportAberturaAlteracao}>
            <CiImport className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnModalImportsEnquadramentoEmpresa && (
        <Tooltip placement="right" title="Carregar Arquivo para Alteração de Enquadramento da Empresa" arrow={mergedArrow}>
          <button className={btnImportBase} onClick={onImportEnquadramento}>
            <CiImport className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnModalImportBaixaEncerramentoEmpresa && (
        <Tooltip placement="right" title="Carregar Arquivo para Baixa ou Encerramento da Empresa" arrow={mergedArrow}>
          <button className={btnImportBase} onClick={onImportBaixaEncerramento}>
            <CiImport className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnCancelarOS && (
        <Tooltip placement="right" title="Cancelar OS" arrow={mergedArrow}>
          <button className={`${styles.btnOS} ${styles.bgColorRed}`} onClick={onCancelar} disabled={loading}>
            <BsFileEarmarkX className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnRestaurarOS && (
        <Tooltip placement="right" title="Restaurar OS" arrow={mergedArrow}>
          <button className={`${styles.btnOS} ${styles.bgColorYellow}`} onClick={onRestaurar} disabled={loading}>
            <BsCaretRightFill className="font-icon" />
          </button>
        </Tooltip>
      )}

      {botoes.isBtnSuspenderOS && (
        <Tooltip placement="right" title="Suspender OS" arrow={mergedArrow}>
          <button className={`${styles.btnOS} ${styles.bgColorYellow}`} onClick={onSuspender} disabled={loading}>
            <CiPause1 className="font-icon" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

export default BarraAcoesOS;